/* ==========================================================================
   Shoppable video showcase
   Scroll-snap rail. No Swiper, no theme slider dependency.
   ========================================================================== */

class ShoppableVideoRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-svs-track]');
    if (!this.track) return;

    this.slides = Array.from(this.querySelectorAll('[data-svs-slide]'));
    if (!this.slides.length) return;

    this.prevBtn = this.querySelector('[data-svs-prev]');
    this.nextBtn = this.querySelector('[data-svs-next]');
    this.dotsEl = this.querySelector('[data-svs-dots]');
    this.autoplay = this.dataset.autoplay === 'true';

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.ticking = false;

    this.onScroll = this.onScroll.bind(this);
    this.onResize = this.onResize.bind(this);

    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.page(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.page(1));

    this.bindVideoControls();
    this.bindDrag();
    this.buildDots();
    this.observeSlides();
    this.update();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
    } else {
      window.addEventListener('resize', this.onResize);
    }
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.onScroll);
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  /* ----- Scrolling ------------------------------------------------------- */

  scrollTo(left) {
    this.track.scrollTo({
      left,
      behavior: this.reducedMotion ? 'auto' : 'smooth',
    });
  }

  page(direction) {
    // One viewport at a time, minus a slide so context carries over.
    const step = Math.max(this.track.clientWidth - this.slideWidth(), this.slideWidth());
    this.scrollTo(this.track.scrollLeft + step * direction);
  }

  slideWidth() {
    const first = this.slides[0];
    if (!first) return this.track.clientWidth;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  maxScroll() {
    return this.track.scrollWidth - this.track.clientWidth;
  }

  onScroll() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.update();
      this.ticking = false;
    });
  }

  onResize() {
    this.buildDots();
    this.update();
  }

  /* ----- Drag to scroll (mouse; touch uses native scrolling) ------------- */

  bindDrag() {
    const track = this.track;
    let pointerId = null;
    let dragging = false;
    let startX = 0;
    let startLeft = 0;
    let distance = 0;

    track.addEventListener('pointerdown', (event) => {
      // Native momentum scrolling is better on touch and pen.
      if (event.pointerType !== 'mouse' || event.button !== 0) return;

      pointerId = event.pointerId;
      dragging = false; // not dragging yet - just pressed
      distance = 0;
      startX = event.clientX;
      startLeft = track.scrollLeft;
    });

    track.addEventListener('pointermove', (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;

      const delta = event.clientX - startX;
      distance = Math.max(distance, Math.abs(delta));

      // Only becomes a drag after real movement - a plain click never
      // sets pointer capture, so links inside the track stay clickable.
      if (!dragging && distance > 5) {
        dragging = true;
        track.classList.add('is-dragging');
        track.setPointerCapture(event.pointerId);
      }

      if (dragging) {
        track.scrollLeft = startLeft - delta;
        event.preventDefault();
      }
    });

    const endDrag = (event) => {
      if (pointerId === null || event.pointerId !== pointerId) return;
      pointerId = null;

      if (!dragging) return; // plain click - let it through untouched

      dragging = false;
      track.classList.remove('is-dragging');

      if (track.hasPointerCapture?.(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }

      // A drag should never trigger the product link underneath it.
      this.suppressClick = true;
      setTimeout(() => {
        this.suppressClick = false;
      }, 0);

      // Settle onto the nearest slide now that snapping is back on.
      const width = this.slideWidth();
      if (width) this.scrollTo(Math.round(track.scrollLeft / width) * width);
    };

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);

    track.addEventListener(
      'click',
      (event) => {
        if (!this.suppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    track.addEventListener('dragstart', (event) => event.preventDefault());
  }

  /* ----- Dots ------------------------------------------------------------ */

  pageCount() {
    if (!this.track.clientWidth) return 1;
    return Math.max(1, Math.ceil(this.track.scrollWidth / this.track.clientWidth));
  }

  buildDots() {
    if (!this.dotsEl) return;

    const count = this.pageCount();

    if (count < 2) {
      this.dotsEl.innerHTML = '';
      this.dotsEl.hidden = true;
      return;
    }

    if (this.dotsEl.childElementCount === count) return;

    this.dotsEl.hidden = false;
    this.dotsEl.innerHTML = '';

    for (let i = 0; i < count; i += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'svs__dot';
      dot.setAttribute('aria-label', `Go to group ${i + 1} of ${count}`);
      dot.addEventListener('click', () => this.scrollTo(i * this.track.clientWidth));
      this.dotsEl.appendChild(dot);
    }
  }

  /* ----- State ----------------------------------------------------------- */

  update() {
    const left = this.track.scrollLeft;
    const max = this.maxScroll();

    if (this.prevBtn) this.prevBtn.disabled = left <= 1;
    if (this.nextBtn) this.nextBtn.disabled = left >= max - 1;

    this.toggleAttribute('data-scrollable', max > 1);

    if (this.dotsEl && !this.dotsEl.hidden) {
      const index = Math.round(left / this.track.clientWidth);
      Array.from(this.dotsEl.children).forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }
  }

  /* ----- Visibility: mark active slide, play only what's on screen -------- */

  observeSlides() {
    if (!('IntersectionObserver' in window)) {
      this.slides.forEach((slide) => slide.classList.add('is-active'));
      return;
    }

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const slide = entry.target;
          const visible = entry.intersectionRatio >= 0.6;

          slide.classList.toggle('is-active', visible);

          const video = slide.querySelector('video');
          if (!video || !this.autoplay) return;

          if (visible) {
            const play = video.play();
            if (play && typeof play.catch === 'function') play.catch(() => {});
          } else {
            video.pause();
          }
        });
      },
      { root: this.track, threshold: [0, 0.6, 1] }
    );

    this.slides.forEach((slide) => this.intersectionObserver.observe(slide));
  }

  /* ----- Play / pause and sound ------------------------------------------ */

  bindVideoControls() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector('video');
      if (!video) return;

      video.muted = true;
      video.setAttribute('playsinline', '');

      const playBtn = slide.querySelector('[data-svs-play]');
      const soundBtn = slide.querySelector('[data-svs-sound]');

      const syncPlayState = () => {
        slide.toggleAttribute('data-playing', !video.paused);
        if (playBtn) {
          playBtn.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
        }
      };

      video.addEventListener('play', syncPlayState);
      video.addEventListener('pause', syncPlayState);
      syncPlayState();

      if (playBtn) {
        playBtn.addEventListener('click', (event) => {
          event.preventDefault();
          if (video.paused) {
            const play = video.play();
            if (play && typeof play.catch === 'function') play.catch(() => {});
          } else {
            video.pause();
          }
        });
      }

      if (soundBtn) {
        soundBtn.addEventListener('click', (event) => {
          event.preventDefault();
          const unmuting = video.muted;

          // Only one slide plays sound at a time.
          if (unmuting) this.muteAll();

          video.muted = !unmuting;
          slide.toggleAttribute('data-sound-on', !video.muted);
          soundBtn.setAttribute('aria-pressed', String(!video.muted));
        });
      }
    });
  }

  muteAll() {
    this.slides.forEach((slide) => {
      const video = slide.querySelector('video');
      if (!video) return;
      video.muted = true;
      slide.removeAttribute('data-sound-on');
      const btn = slide.querySelector('[data-svs-sound]');
      if (btn) btn.setAttribute('aria-pressed', 'false');
    });
  }
}

if (!customElements.get('shoppable-video-rail')) {
  customElements.define('shoppable-video-rail', ShoppableVideoRail);
}